// Used in login-routing.module.ts

// Angular
import { Component, OnInit } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { CookieService } from "#core/services/cookie.service";
import { FormHelper } from "#helpers/form.helper";
import { LocalStorage } from "#services/local-storage.service";

// RXJS/Store
import { Store, select } from "@ngrx/store";
import { Observable } from "rxjs";
import { first } from "rxjs/operators";
import * as fromStore from "../../store";

// Validators
import { EmailValidator } from "#validators/email.validator";

// interfaces
import { Title } from "@angular/platform-browser";
import { Auth, Login } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "app-login-form",
    templateUrl: "./login-form.component.html",
    styleUrls: ["./login-form.component.scss"],
    standalone: false
})
export class LoginFormComponent implements OnInit {
    loginForm = this.fb.group({
        email: [
            "",
            {
                validators: [Validators.required, EmailValidator.email],
                updateOn: "change",
            },
        ],
        password: [
            "",
            {
                validators: [Validators.required],
                updateOn: "change",
            },
        ],
    });
    hide = true; // Password visibility
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$: Observable<boolean>;
    bannerMessage$: Observable<string>;
    bannerColor$: Observable<string>;
    navLinks = [
        { label: "Login", path: "../form", id: "login-tab" },
        { label: "Register", path: "../register", id: "registration-tab" },
    ];

    noTokenPresent = false;
    noTokenMessage = "Please login through MyCL via the button below.";

    // Demo ONLY
    isDemo = (<any>window).isDemo;

    constructor(
        private auth: Auth,
        private fb: UntypedFormBuilder,
        private localStorage: LocalStorage,
        private router: Router,
        public store: Store<fromStore.UserState>,
        public formHelper: FormHelper,
        private titleService: Title,
        private cookieService: CookieService
    ) {
        this.bannerStatus$ = store.pipe(select(fromStore.getLoginBannerStatus));
        this.bannerMessage$ = store.pipe(
            select(fromStore.getLoginBannerMessage)
        );
        this.bannerColor$ = store.pipe(select(fromStore.getLoginBannerColor));
    }

    ngOnInit() {
        // PAS-4514 Sample Center needs to specify a redirect
        // on logout to finish the lifecycle of a user.
        const logoutRedirectURL = this.localStorage.get("logoutRedirectURL");
        if (
            logoutRedirectURL !== undefined &&
            logoutRedirectURL !== null &&
            logoutRedirectURL !== "null" &&
            logoutRedirectURL !== ""
        ) {
            this.localStorage.clear();
            if ((<any>window).isDemo) {
                this.store.dispatch(fromStore.userSampleCenterRedirect());
            } else {
                this.store.dispatch(fromStore.userMyCLRedirect());
            }
            (<any>window).location.href = logoutRedirectURL;
        }

        if (this.maintenanceMode) {
            this.store.dispatch(fromStore.userLoginMaintenance());
        } else {
            this.bannerMessage$.pipe(first()).subscribe((message: string) => {
                if (
                    !message.includes("Session ended") &&
                    !message.includes("Sample Center")
                ) {
                    this.store.dispatch(fromStore.userLoginResetState());
                }
            });
        }

        this.titleService.setTitle("Carnegie Learning - Login");

        // Legacy v5 cleanup, remove non-wildcard (*) domain CL_TOKEN2
        // TODO remove this when v5 is forgotten in the anals of history
        document.cookie =
            "CL_TOKEN2=;expires=Thu, 01 Jan 1970 00:00:00 UTC; path=" +
            (<any>window).passportHost +
            ";";

        const authenticationToken = this.cookieService.get("CL_TOKEN2");
        if (
            authenticationToken === undefined ||
            authenticationToken === "" ||
            authenticationToken === null
        ) {
            // user is not authenticated and no redirect URL is present so going to display a generic warning
            this.noTokenPresent = true;
        } else {
            this.store.dispatch(fromStore.userProfileAttempt());
        }
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.loginForm, field, type);
    }

    markAsTouched(event: KeyboardEvent) {
        this.loginForm.markAsTouched();
    }

    login() {
        if (this.loginForm.valid) {
            // prevents the enter button from triggering request
            this.loginForm.markAsUntouched();
            const payload: Login = {
                login: this.loginForm.get("email")?.value,
                password: this.loginForm.get("password")?.value,
            };

            this.store.dispatch(
                fromStore.userLoginAttempt({ payload: payload })
            );
        }
    }

    forgotPassword() {
        this.router.navigate(["login/forgot-pwd"]);
    }

    goToSupportForm() {
        const supportURL =
            "https://www.carnegielearning.com/lets-talk/#support";
        window.location.href = supportURL;
    }

    // Remove whitespace from login email
    removeWhitespace(event: KeyboardEvent) {
        const email = this.loginForm.value.email;
        this.loginForm.patchValue({
            email: email.trim(),
        });
    }

    // most odd authentication state, add a button to return the user
    returnToMyCL() {
        this.localStorage.clear();
        this.store.dispatch(fromStore.userMyCLRedirect());
        this.noTokenPresent = false;
        (<any>window).location.href = (<any>window).FIDO_PORTAL_RETURN_URL;
    }
}
